import React, { useEffect, useState } from "react";

import { find } from "lodash/fp";
import { useNavigate, useLocation } from "react-router-dom";

import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { Alert, Stack, Typography } from "@mui/material";

import AuthButton from "@app/analytics/auth/components/AuthButton";
import useAccountSettings from "@app/analytics/hooks/settings/useAccountSettings";
import useWorkspaceInvite from "@app/analytics/hooks/workspace/useWorkspaceInvite";
import useAuth from "@app/common/auth/useAuth";
import Link from "@app/common/components/Link";
import useHasMeetings from "@app/common/hooks/meetings/useHasMeetings";
import { showToast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import { isResponseErrorWithStatus, withQueryParams } from "@app/common/utils";
import { getUserFullName } from "@app/common/utils";
import { EnterpriseWorkspaceInvite } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isWorkspaceOwner } from "@app/common/workspaces/workspaceUtils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import AlertBanner from "@rui/AlertBanner";
import LoadSpinner from "@rui/LoadSpinner";
import TooltipWrapper from "@rui/TooltipWrapper";

import WorkspaceInviteConfirmationModal from "./modals/WorkspaceInviteConfirmationModal";

export type ModalAction = "accept" | "decline";

const getInviterString = ({
  inviterFirstName,
  inviterLastName,
  inviterEmail,
  workspaceName,
  teamName,
}: EnterpriseWorkspaceInvite) => {
  const inviterFullName = inviterFirstName
    ? getUserFullName({
        firstName: inviterFirstName,
        lastName: inviterLastName ?? "",
      })
    : null;

  const inviterString = inviterFullName ?? inviterEmail;
  const invitePrefix = inviterString
    ? `${inviterString} invited you`
    : "You have been invited";

  return `${invitePrefix} to join the ${
    teamName ? `${teamName} team` : `${workspaceName} workspace`
  }.`;
};

const WorkspaceJoin = () => {
  usePageTitle("Join Workspace");

  const { setWorkspaceId, inWorkspace, workspace, workspaceId, workspaceUser } =
    useWorkspace();

  const { invites, pendingInvite, isLoading, updateInvite } =
    useWorkspaceInvite();
  const navigate = useNavigate();
  const { resendEmailVerification } = useAccountSettings();
  const location = useLocation();
  const { user, fetchAndSetUser } = useAuth();

  const { hasMeetings } = useHasMeetings({ userId: user!.id });

  const [inviteId, setInviteId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<ModalAction>("decline");
  const isOwner = isWorkspaceOwner(workspaceUser);

  const invite = pendingInvite ?? find({ id: inviteId }, invites);

  // Set the pending invite id so we can
  // fetch the pending invite from the
  // invites list once it's been accepted
  useEffect(() => {
    if (pendingInvite) {
      setInviteId(pendingInvite.id);
    }
  }, [pendingInvite]);

  const onConfirm = (action: ModalAction) => {
    if (!invite) return;

    setLoading(true);
    updateInvite(
      { inviteId: invite.id, isAccepted: action === "accept" },
      {
        onError: () => setLoading(false),
        onSuccess: () => {
          navigate(ROUTES.USER_SETTINGS_DEFAULT_SECTION);
          showToast({
            message: `You have ${
              action === "accept" ? "accepted" : "declined"
            } the invite to the ${invite.workspaceName} Workspace.`,
          });
        },
      }
    );
  };

  const handleActionClick = (action: ModalAction) => {
    if (!invite) return;

    if (action === "decline") {
      setModalAction(action);
      setModalOpen(true);
    } else {
      setLoading(true);
      updateInvite(
        { inviteId: invite.id, isAccepted: action === "accept" },
        {
          onError: (error: any) => {
            setLoading(false);
            if (isResponseErrorWithStatus(error, 402)) {
              showToast({
                severity: "error",
                message:
                  "Sorry! This Workspace is currently full. Please reach out to the Workspace owner to add additional seats.",
              });
            } else {
              showToast({
                severity: "error",
                message:
                  "Unable to join Workspace. Please refresh the page or try again later.",
              });
            }
          },
          onSuccess: async () => {
            const user = await fetchAndSetUser();
            if (user.workspaceId) {
              setWorkspaceId(user.workspaceId);
            }
            navigate(
              withQueryParams(
                ROUTES.workspaceSettingsById(invite.workspaceId),
                { migrateMeetings: hasMeetings ? true : undefined }
              )
            );
            showToast({
              message: `Success! You have joined the ${invite.workspaceName} Workspace.`,
              severity: "success",
            });
          },
        }
      );
    }
  };

  useEffect(() => {
    const isAutoAccept = location.pathname.includes("join");
    const canAutoAccept =
      isAutoAccept && !inWorkspace && !isLoading && inviteId;

    if (canAutoAccept && !loading) {
      handleActionClick("accept");
    }
  }, [isLoading, inviteId, isOwner, inWorkspace, loading]);

  useEffect(() => {
    if (isLoading || invite) return;

    showToast({
      message: "This Workspace invite is no longer valid.",
      severity: "error",
    });

    const redirectPath = inWorkspace
      ? ROUTES.workspaceSettingsById(workspaceId!)
      : ROUTES.USER_SETTINGS_DEFAULT_SECTION;

    navigate(redirectPath);
  }, [isLoading, invite, inWorkspace, workspaceId]);

  if (isLoading || !invite) {
    return (
      <Stack
        sx={{ position: "relative", flexGrow: 1, bgcolor: "#FFF" }}
        alignItems="center"
        justifyContent="center"
      >
        <LoadSpinner />
      </Stack>
    );
  }

  return (
    <>
      {!user?.isEmailVerified && (
        <AlertBanner
          severity="warning"
          title="Email verification"
          action={{
            label: "Resend Verification",
            onClick: () => resendEmailVerification.mutate(),
          }}
        >
          You must verify your email address before joining a Workspace. Please
          click the magic link in your inbox to verify your email.
        </AlertBanner>
      )}
      <Stack sx={{ position: "relative", flexGrow: 1, bgcolor: "#FFF" }}>
        <Stack
          direction="column"
          sx={{ p: 3, pt: "68px" }}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Stack
            sx={{ width: "100%", maxWidth: "750px", padding: "40px" }}
            spacing={3}
          >
            <Typography variant="h1" sx={{ color: "shades.neutral.800" }}>
              You've been invited to join the {invite.workspaceName} Workspace
            </Typography>
            <Typography variant="bodyPrimaryReg">
              Workspaces allow companies to build holistic views of meeting
              metrics, and allow for custom permissions across teams and
              individuals.{" "}
              <Link
                href={LINKS.MARKETING.WORKSPACES}
                sx={{
                  color: "shades.primary.500",
                  textDecoration: "underline",
                }}
              >
                Learn More
              </Link>
              .
            </Typography>
            <Typography
              variant="bodyPrimaryMed"
              sx={{ color: "shades.primary.500" }}
            >
              {getInviterString(invite)}
            </Typography>
            {inWorkspace && !isOwner && (
              <Alert
                severity="warning"
                sx={{ alignItems: "center" }}
                icon={
                  <ErrorOutlineRounded sx={{ color: "shades.orange.700" }} />
                }
              >
                <Typography
                  variant="bodySecondaryMed"
                  sx={{ color: "shades.orange.700" }}
                >
                  You are only able to belong to one Workspace at a time.
                  Accepting this invitation will remove you from your existing
                  Workspace, {workspace.name}
                </Typography>
              </Alert>
            )}
            {isOwner && (
              <Alert
                severity="warning"
                sx={{ alignItems: "center" }}
                icon={
                  <ErrorOutlineRounded sx={{ color: "shades.orange.700" }} />
                }
              >
                <Typography
                  variant="bodySecondaryMed"
                  sx={{ color: "shades.orange.700" }}
                >
                  You are already the owner of a Workspace. You must delete or
                  transfer ownership of your existing Workspace before you can
                  join a new one. You can do so in your{" "}
                  <Link to={ROUTES.TRANSFER_WORKSPACE}>
                    Workspace settings.
                  </Link>
                </Typography>
              </Alert>
            )}
            <Stack direction="row" spacing={2}>
              <TooltipWrapper
                title="You must first verify your email to join this Workspace."
                show={!user?.isEmailVerified}
              >
                <AuthButton
                  loading={loading}
                  color="primary"
                  sx={{ width: "160px", height: "48px" }}
                  onClick={() => handleActionClick("accept")}
                  disabled={isOwner || !user?.isEmailVerified}
                >
                  Accept
                </AuthButton>
              </TooltipWrapper>
              <AuthButton
                variant="text"
                onClick={() => handleActionClick("decline")}
                sx={{
                  width: "fit-content",
                  height: "48px",
                  px: 2,
                  color: "shades.primary.500",
                }}
              >
                Decline
              </AuthButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <WorkspaceInviteConfirmationModal
        open={modalOpen}
        action={modalAction}
        onClose={() => setModalOpen(false)}
        onConfirm={() => onConfirm(modalAction)}
        loading={loading}
        confirmProps={{
          label: modalAction === "accept" ? "Accept Invite" : "Decline Invite",
        }}
      />
    </>
  );
};

export default WorkspaceJoin;
